import {Header, Pagination} from '@/components';
import type {PaginationProps} from '@/components/common/Pagination';
import {TableSkeleton} from '@/components/Table/TableSkeleton';

import InvoicesFilters from './InvoicesFilters';
import InvoicesTable from './InvoicesTable';
import useInvoiceList from './useInvoiceList';
import useMerchantOptions from './useMerchantOptions';

/**
 * Fee invoices raised against merchants. Its own entry under Merchants, next to
 * Fees. The merchant options are loaded here and shared by the filter dropdown
 * and the create form.
 */
const MerchantInvoiceList = () => {
  const {
    isLoading,
    list,
    pageInfo,
    fetchList,
    refetchCurrentPage,
    displayPage,
    isPageReset,
    filterCount,
    filtersToApply,
    clearFilters,
    selectAppliedFilters,
    handleApplyFilters,
  } = useInvoiceList();

  const {merchants, isLoading: isLoadingMerchants} = useMerchantOptions();

  const renderList = () => {
    if (isLoading) {
      return <TableSkeleton />;
    }

    if (!list.length) {
      return (
        <p
          className="py-10 text-center text-gray-500 dark:text-gray-400"
          data-cy="merchant-invoices-empty"
        >
          No invoices found.
        </p>
      );
    }

    return (
      <InvoicesTable
        items={list}
        onInvoiceUpdated={refetchCurrentPage}
        dataCy="merchant-invoices"
      />
    );
  };

  return (
    <main className="my-5 mx-4 md:mx-10">
      <Header title="Merchant Invoices" dataCy="merchant-invoices-title" />

      <InvoicesFilters
        filterCount={filterCount}
        filtersToApply={filtersToApply}
        clearFilters={clearFilters}
        selectAppliedFilters={selectAppliedFilters}
        handleApplyFilters={handleApplyFilters}
        merchants={merchants}
        isLoadingMerchants={isLoadingMerchants}
        onInvoiceSaved={refetchCurrentPage}
      />

      <section className="pt-5 md:pt-5">{renderList()}</section>

      {list && list.length ? (
        <Pagination
          onPageChange={fetchList as PaginationProps['onPageChange']}
          total={pageInfo.total}
          isPageReset={isPageReset}
          currentPageOverride={displayPage}
        />
      ) : null}
    </main>
  );
};

export default MerchantInvoiceList;
